import React, { useContext, useEffect, useState } from 'react';
import {
  FaBook,
  FaBriefcase,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaCode,
  FaFileAlt,
  FaPencilAlt,
} from 'react-icons/fa';
import { ApplicationTrackerContext } from '../../contexts/ApplicationTrackerContext';
import { useAuth } from '../../contexts/AuthContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';
import ApplicationTaskModal from './ApplicationTaskModal';
import EditProgressModal from './EditProgressModal';
import LeetCodeHelpModal from './LeetCodeHelpModal';
import LeetCodeProblemModal from './LeetCodeProblemModal';
import LessonTaskModal from './LessonTaskModal';
import ResumeTaskHelpModal from './ResumeTaskHelpModal';
import QuestWeekCountdown from './components/QuestWeekCountdown';

const isLessonUnlocked = (lessonName, nodes, completedNodes) => {
  if (!lessonName) return false;

  // Find the node that matches this lesson name
  const node = Object.values(nodes).find((node) => node.title === lessonName);
  if (!node) return false;

  const lessonId = node.id;
  if (lessonId === 'start') return true;

  // Find parents of this node
  const parents = Object.values(nodes)
    .filter((node) => node.children && node.children.includes(lessonId))
    .map((node) => node.id);

  // Node is available if at least one parent is completed
  return parents.some((parentId) => completedNodes.includes(parentId));
};

const TaskItem = ({ task, onToggle }) => {
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showLeetCodeHelpModal, setShowLeetCodeHelpModal] = useState(false);
  const [showLeetCodeProblemModal, setShowLeetCodeProblemModal] = useState(false);
  const [showLessonTaskModal, setShowLessonTaskModal] = useState(false);
  const [showApplicationTaskModal, setShowApplicationTaskModal] = useState(false);
  const { completedNodes, nodes } = useContext(GlobalContext);
  const { setAddApplicationModalOpen } = useContext(ApplicationTrackerContext);
  const Icon = task.icon;

  const handleToggle = () => {
    if (task.type === 'leetcode' && !task.completed) {
      setShowLeetCodeProblemModal(true);
    } else if (task.type === 'lessons' && !task.completed) {
      setShowLessonTaskModal(true);
    } else if (task.type === 'apps' && !task.completed) {
      // setAddApplicationModalOpen(true);
      setShowApplicationTaskModal(true);
    } else {
      onToggle(task.id);
    }
  };

  const handleLeetCodeProblemSubmit = (problemName) => {
    onToggle(task.id, problemName);
  };

  return (
    <>
      <div
        className={`p-4 rounded-lg transition-colors flex items-center ${
          task.completed ? 'bg-green-50' : 'bg-gray-50 hover:bg-gray-100'
        }`}
      >
        <button
          onClick={handleToggle}
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
            task.completed
              ? 'border-green-500 bg-green-500'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          {task.completed && <FaCheck className="text-white text-xs" />}
        </button>
        <div className="flex-1 ml-4">
          {task.type === 'apps' ? (
            <>
              <p className="text-sm font-medium text-gray-800">
                {task.completed ? 'Applied to' : 'Apply to a job'} {task.company_name}
              </p>
            </>
          ) : task.type === 'leetcode' ? (
            <>
              <p className="text-sm font-medium text-gray-800">
                {task.completed ? 'Did Leetcode problem:' : 'Do a Leetcode problem'}{' '}
                {task.name ? `: ${task.name}` : ''}
              </p>
            </>
          ) : task.type === 'lessons' ? (
            <>
              <p className="text-sm font-medium text-gray-800">
                {task.completed ? 'Completed' : 'Complete'} "{task.lesson_name}" lesson
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-800">
                {task.name || task.lesson_name || task.task_name || 'Untitled Task'}
              </p>
              {task.difficulty && <p className="text-xs text-gray-500">{task.difficulty}</p>}
            </>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {task.type === 'resume' && (
            <button
              onClick={() => setShowHelpModal(true)}
              className="px-2 py-1 bg-orange-100 text-orange-600 text-xs font-medium rounded-full hover:bg-orange-200 transition-colors flex items-center space-x-1"
              title="Get help with this task"
            >
              <span>Teach me how</span>
              <span>→</span>
            </button>
          )}
          {task.type === 'leetcode' && (
            <button
              onClick={() => setShowLeetCodeHelpModal(true)}
              className="px-2 py-1 bg-blue-100 text-blue-600 text-xs font-medium rounded-full hover:bg-blue-200 transition-colors flex items-center space-x-1"
              title="Get help with LeetCode problems"
            >
              <span>Teach me how</span>
              <span>→</span>
            </button>
          )}
          {task.type === 'lessons' &&
            (isLessonUnlocked(task.lesson_name, nodes, completedNodes) ? (
              <a
                href={
                  Object.values(nodes).find((node) => node.title === task.lesson_name)?.path ||
                  `/progress-tree`
                }
                className="px-2 py-1 bg-purple-100 text-purple-600 text-xs font-medium rounded-full hover:bg-purple-200 transition-colors flex items-center space-x-1"
                title="Go to lesson"
              >
                <span>Start lesson</span>
                <span>→</span>
              </a>
            ) : (
              <a
                href="/progress-tree"
                className="px-2 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full hover:bg-gray-200 transition-colors flex items-center space-x-1"
                title="Go to progress tree to unlock this lesson"
              >
                <span>Unlock lesson</span>
                <span>🔒</span>
              </a>
            ))}
          <div className={`p-2 rounded-full bg-${task.color}-100`}>
            <Icon className={`text-${task.color}-500`} />
          </div>
        </div>
      </div>
      {task.type === 'resume' && (
        <ResumeTaskHelpModal
          isOpen={showHelpModal}
          onClose={() => setShowHelpModal(false)}
          taskName={task.task_name}
        />
      )}
      {task.type === 'leetcode' && (
        <>
          <LeetCodeHelpModal
            isOpen={showLeetCodeHelpModal}
            onClose={() => setShowLeetCodeHelpModal(false)}
          />
          <LeetCodeProblemModal
            isOpen={showLeetCodeProblemModal}
            onClose={() => setShowLeetCodeProblemModal(false)}
            onSubmit={handleLeetCodeProblemSubmit}
          />
        </>
      )}
      {task.type === 'lessons' && (
        <LessonTaskModal
          isOpen={showLessonTaskModal}
          onClose={() => setShowLessonTaskModal(false)}
        />
      )}
      {task.type === 'apps' && (
        <ApplicationTaskModal
          isOpen={showApplicationTaskModal}
          onClose={() => setShowApplicationTaskModal(false)}
          companyName={task.company_name}
        />
      )}
    </>
  );
};

const ProgressSummary = ({ tasks, onEditClick }) => {
  const getProgress = (type) => {
    const typeTasks = tasks.filter((task) => task.type === type);
    if (typeTasks.length === 0) return { completed: 0, total: 0 };
    const completed = typeTasks.filter((task) => task.completed).length;
    return { completed, total: typeTasks.length };
  };

  const progressItems = [
    { type: 'lessons', label: 'Lessons', icon: FaBook, color: 'purple' },
    { type: 'resume', label: 'Resume', icon: FaFileAlt, color: 'orange' },
    { type: 'apps', label: 'Applications', icon: FaBriefcase, color: 'green' },
    { type: 'leetcode', label: 'Leetcode', icon: FaCode, color: 'blue' },
  ];

  // Filter out items with no tasks
  const visibleItems = progressItems.filter(({ type }) => {
    const { total } = getProgress(type);
    return total > 0;
  });

  // Calculate grid columns based on number of visible items
  const gridCols =
    {
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
      4: 'grid-cols-4',
    }[visibleItems.length] || 'grid-cols-1';

  return (
    <div className={`grid ${gridCols} gap-4 mb-6`}>
      {visibleItems.map(({ type, label, icon: Icon, color }) => {
        const { completed, total } = getProgress(type);
        const percentage = Math.round((completed / total) * 100);

        return (
          <button
            key={type}
            onClick={() => onEditClick(type)}
            className={`bg-${color}-50 rounded-lg p-4 text-left hover:bg-${color}-100 transition-colors`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className={`p-2 rounded-full bg-${color}-100`}>
                  <Icon className={`text-${color}-500`} />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">{label}</div>
                  <div className="text-xs text-gray-500">
                    {completed}/{total}
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full bg-${color}-500`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </button>
        );
      })}
    </div>
  );
};

const DatePicker = ({ onDateChange, defaultDate }) => {
  // Format date to YYYY-MM-DD for the input
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="flex items-center space-x-2">
      <input
        type="date"
        defaultValue={formatDateForInput(defaultDate)}
        onChange={(e) => onDateChange(new Date(e.target.value))}
        className="px-2 py-1 rounded-md border border-gray-300 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );
};

const calculateWeekEnd = () => {
  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday
  const daysUntilSaturday = (6 - currentDay) % 7; // Days until next Saturday

  const weekEnd = new Date(now);
  weekEnd.setDate(now.getDate() + daysUntilSaturday);
  weekEnd.setHours(23, 59, 59, 999);

  // If it's already Saturday, set to end of today
  if (currentDay === 6) {
    weekEnd.setDate(now.getDate());
  }

  return weekEnd;
};

const QuestMiddleColumn = () => {
  const { questPlan, fetchQuest, nodes, completedNodes } = useContext(GlobalContext);
  const { user } = useAuth();
  const [currentWeekTasks, setCurrentWeekTasks] = useState([]);
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Check if user is allowed to control weeks
  const canControlWeeks = user?.email === 'smao@g.hmc.edu';

  useEffect(() => {
    if (!questPlan?.quest_plan) return;

    // Find the current week's tasks based on selected date
    let currentWeek = questPlan.quest_plan.find((week, index) => {
      const weekStart = new Date(week.week_start);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      if (selectedDate >= weekStart && selectedDate < weekEnd) {
        setCurrentWeekIndex(index);
        return true;
      }
      return false;
    });

    // If no current week found, use the first week
    if (!currentWeek && questPlan.quest_plan.length > 0) {
      currentWeek = questPlan.quest_plan[0];
      setCurrentWeekIndex(0);
    }

    if (!currentWeek) return;

    // Format tasks for display
    const formattedTasks = [];

    // Add LeetCode tasks
    currentWeek.tasks.leetcode.forEach((task, index) => {
      formattedTasks.push({
        id: `leetcode-${index}`,
        type: 'leetcode',
        name: task.problem_name,
        completed: task.completed,
        icon: FaCode,
        color: 'blue',
        taskIndex: index,
      });
    });

    // Add application tasks
    currentWeek.tasks.apps.forEach((task, index) => {
      formattedTasks.push({
        id: `apps-${index}`,
        type: 'apps',
        company_name: task.company_name,
        completed: task.completed,
        icon: FaBriefcase,
        color: 'green',
        taskIndex: index,
      });
    });

    // Add lesson tasks
    currentWeek.tasks.lessons.forEach((task, index) => {
      formattedTasks.push({
        id: `lessons-${index}`,
        type: 'lessons',
        lesson_name: task.lesson_name,
        completed: task.completed,
        icon: FaBook,
        color: 'purple',
        taskIndex: index,
      });
    });

    // Add resume tasks
    currentWeek.tasks.resume.forEach((task, index) => {
      formattedTasks.push({
        id: `resume-${index}`,
        type: 'resume',
        task_name: task.task_name,
        completed: task.completed,
        icon: FaFileAlt,
        color: 'orange',
        taskIndex: index,
      });
    });

    setCurrentWeekTasks(formattedTasks);
  }, [questPlan, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleToggleTask = async (taskId, problemName = null) => {
    if (!questPlan?.quest_id) return;

    // Parse task ID to get type and index
    const [type, index] = taskId.split('-');

    const task = currentWeekTasks.find((t) => t.id === taskId);
    console.log('Toggling task:', { taskId, type, task }); // Debug log

    if (!task) return;

    try {
      const requestBody = {
        task_type: type,
        task_name: problemName || task.name || task.lesson_name || task.task_name || '',
        completed: !task.completed,
        week_index: currentWeekIndex,
        task_index: task.taskIndex,
      };

      const response = await axios.put(`/api/v1/quest/${questPlan.quest_id}/task`, requestBody);
      console.log('Response data:', response.data); // Debug log

      // Update local state
      setCurrentWeekTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.id === taskId ? { ...t, completed: !t.completed, name: problemName || t.name } : t
        )
      );
      fetchQuest();
    } catch (error) {
      console.error('Error updating task:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleEditClick = (taskType) => {
    setSelectedTaskType(taskType);
    setShowEditModal(true);
  };

  if (!questPlan?.quest_plan) {
    return (
      <div className="md:col-span-5 flex flex-col space-y-6 h-full">
        <div className="bg-white p-6 rounded-lg shadow-md flex-1 overflow-hidden">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">This week's progress</h2>
          </div>
          <div className="flex items-center justify-center h-[calc(100%-3rem)]">
            <p className="text-gray-500">No quest plan available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="md:col-span-5 flex flex-col space-y-6 h-full">
      <div className="bg-white p-6 rounded-lg shadow-md flex-1 overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-semibold">
              Week {currentWeekIndex + 1} of {questPlan.quest_plan.length} of your Quest 🗺️
            </h2>
            {/* Week countdown timer */}
            {(() => {
              const currentWeek = questPlan.quest_plan[currentWeekIndex];
              if (currentWeek) {
                const weekEnd = calculateWeekEnd();
                const weekCompleted =
                  currentWeekTasks.length > 0 && currentWeekTasks.every((task) => task.completed);

                const handleWeekReset = () => {
                  // Refresh quest data when week resets
                  fetchQuest();
                };

                return (
                  <div className="flex items-center h-full">
                    <QuestWeekCountdown
                      weekEndDate={weekEnd}
                      weekCompleted={weekCompleted}
                      onWeekReset={handleWeekReset}
                    />
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>
        {canControlWeeks && (
          <DatePicker onDateChange={handleDateChange} defaultDate={selectedDate} />
        )}

        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-medium text-gray-700 font-semibold">Progress so far</h2>
          <button
            onClick={() => setShowEditModal(true)}
            className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full hover:bg-gray-200 transition-colors flex items-center space-x-1"
          >
            <span>View/edit all</span>
            <FaPencilAlt className="text-xs" />
          </button>
        </div>

        {currentWeekTasks.length > 0 && (
          <ProgressSummary tasks={currentWeekTasks} onEditClick={handleEditClick} />
        )}
        <h2 className="text-lg font-medium text-gray-700 font-semibold">
          {currentWeekTasks.filter((task) => !task.completed).length} remaining tasks this week
        </h2>
        <p className="text-gray-500 mb-2">
          {' '}
          Start with{' '}
          {Math.ceil(
            currentWeekTasks.filter((task) => !task.completed).length /
              Math.max(1, 7 - new Date().getDay())
          )}{' '}
          today!
        </p>
        <div className="space-y-3 overflow-y-auto flex-1 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {currentWeekTasks.length > 0 ? (
            <>
              {currentWeekTasks
                .sort((a, b) => {
                  // First sort by type
                  const typeOrder = { lessons: 1, resume: 2, apps: 3, leetcode: 4 };
                  const typeComparison = typeOrder[a.type] - typeOrder[b.type];

                  if (typeComparison !== 0) return typeComparison;

                  // If both are lessons, sort by unlocked status
                  if (a.type === 'lessons' && b.type === 'lessons') {
                    const aUnlocked = isLessonUnlocked(a.lesson_name, nodes, completedNodes);
                    const bUnlocked = isLessonUnlocked(b.lesson_name, nodes, completedNodes);
                    if (aUnlocked !== bUnlocked) {
                      return aUnlocked ? -1 : 1; // Unlocked lessons come first
                    }
                  }

                  return 0;
                })
                .filter((task) => !task.completed)
                .slice(
                  0,
                  showAllTasks
                    ? undefined
                    : Math.ceil(
                        currentWeekTasks.filter((task) => !task.completed).length /
                          Math.max(1, 7 - new Date().getDay())
                      )
                )
                .map((task) => (
                  <TaskItem key={task.id} task={task} onToggle={handleToggleTask} />
                ))}
              {currentWeekTasks.filter((task) => !task.completed).length > 3 && (
                <button
                  onClick={() => setShowAllTasks(!showAllTasks)}
                  className="w-full py-2 text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center space-x-2"
                >
                  {showAllTasks ? (
                    <>
                      <span>Show Less</span>
                      <FaChevronUp className="text-xs" />
                    </>
                  ) : (
                    <>
                      <span>Show more tasks for this week</span>
                      <FaChevronDown className="text-xs" />
                    </>
                  )}
                </button>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">No tasks for this week</p>
            </div>
          )}
        </div>
      </div>
      <EditProgressModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTaskType(null);
        }}
        tasks={
          selectedTaskType
            ? currentWeekTasks.filter((task) => task.type === selectedTaskType)
            : currentWeekTasks
        }
        onToggleTask={handleToggleTask}
      />
    </div>
  );
};

export default QuestMiddleColumn;
